import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'reactstrap';
import { useConfirmApi } from 'utils/api/ApiHooks';
import SimpleErrorPage from 'views/Error/SimpleErrorPage';
import Page from 'views/Layout/Pages/Page';
import EmptyState from 'views/Widgets/EmptyState';
import Loading from 'views/Widgets/Loading';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import { useAIAgentsHub } from '../AiAgentsHubContext';
import EditWorkflowModal, { WorkflowItem } from './EditWorkflowModal';

const WorkflowsAdminPage: FC = () => {
  const { setAction } = useAIAgentsHub();
  const { formatMessage } = useIntl();
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowItem | null>(
    null
  );

  const [workflows, setWorkflows] = useState<WorkflowItem[]>([]);

  const { data, status } = useConfirmApi<WorkflowItem[]>({
    url: 'ai-workflows',
    method: 'GET',
  });

  useEffect(() => {
    if (data) {
      setWorkflows(data);
    }
  }, [data]);

  // No action button for this tab
  useEffect(() => {
    setAction(null);
  }, [setAction]);

  const handleEditClick = useCallback((workflow: WorkflowItem) => {
    setSelectedWorkflow(workflow);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedWorkflow(null);
  }, []);

  const handleSaveSuccess = useCallback((updated: WorkflowItem) => {
    setWorkflows((prev) =>
      prev.map((w) =>
        w.workflow_type === updated.workflow_type ? { ...w, ...updated } : w
      )
    );
    setSelectedWorkflow(null);
  }, []);

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.table.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        className: 'col-2',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.table.description',
          defaultMessage: 'Description',
        }),
        field: 'description',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.table.instructions',
          defaultMessage: 'Custom Instructions',
        }),
        field: 'instructions',
        sortable: false,
        showFullLengthHeader: true,
      },
      {
        name: formatMessage({
          id: 'app.views.ai.agents.workflows_admin.table.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-1',
        sortable: false,
      },
    ],
    [formatMessage]
  );

  const rows = useMemo(
    () =>
      workflows.map((workflow) => ({
        id: workflow.workflow_type,
        name: workflow.workflow_type_display,
        description: workflow.description,
        instructions: workflow.instructions ? (
          <span className="text-muted">
            {workflow.instructions.substring(0, 100)}
            {workflow.instructions.length > 100 ? '...' : ''}
          </span>
        ) : (
          <em className="text-muted">
            <FormattedMessage
              id="app.views.ai.agents.workflows_admin.no_instructions"
              defaultMessage="No custom instructions"
            />
          </em>
        ),
        actions: (
          <Button
            size="sm"
            color="white"
            onClick={() => handleEditClick(workflow)}
          >
            <i className="fe fe-edit-2" />
          </Button>
        ),
      })),
    [workflows, handleEditClick]
  );

  if (status === 'LOADING') {
    return (
      <Loading
        layout="overlay"
        message={formatMessage({
          id: 'app.views.ai.agents.workflows_admin.loading',
          defaultMessage: 'Loading workflows...',
        })}
      />
    );
  }

  if (status === 'ERROR') {
    return <SimpleErrorPage />;
  }

  return (
    <Page
      embedded={true}
      title={formatMessage({
        id: 'app.views.ai.agents.workflows_admin.title',
        defaultMessage: 'Workflows',
      })}
    >
      {workflows.length === 0 ? (
        <EmptyState
          title={formatMessage({
            id: 'app.views.ai.agents.workflows_admin.empty.title',
            defaultMessage: 'No workflows available',
          })}
          subtitle={formatMessage({
            id: 'app.views.ai.agents.workflows_admin.empty.subtitle',
            defaultMessage:
              'AI workflows will appear here once the relevant features are enabled.',
          })}
        />
      ) : (
        <FilterablePeopleTable
          columns={columns}
          rows={rows}
          hideFilters={true}
          hideExportButton={true}
        />
      )}

      {selectedWorkflow && (
        <EditWorkflowModal
          isOpen={!!selectedWorkflow}
          workflow={selectedWorkflow}
          toggle={handleCloseModal}
          onSuccess={handleSaveSuccess}
        />
      )}
    </Page>
  );
};

export default React.memo(WorkflowsAdminPage);
